import { useEffect, useRef, useState } from "react";

import { ILocalizedTestElement } from "@/modules/assessments/types/test.interface";
import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";

export function useAssessmentInstructionsHook({
  inviteId,
  testId,
  currentPage,
  questionsList,
}: {
  inviteId?: number;
  testId: number;
  currentPage: number;
  questionsList: ILocalizedTestElement[][];
}) {
  const [isOpenedInstructions, setIsOpenedInstructions] = useState(false);

  // Instructions arrive already resolved to the invite locale, keyed by testId.
  const [testInstructions, setTestInstructions] = useState<{ [testId: string]: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // A transient fetch failure would otherwise leave the whole assessment without instructions:
  // testId in the deps re-arms the fetch at each test transition until an attempt succeeds, and
  // the refs keep transitions from stacking duplicate requests or refetching after success.
  const hasLoadedRef = useRef(false);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const fetchInstructions = async (id: number) => {
      isFetchingRef.current = true;
      try {
        setIsLoading(true);
        const instructions = await InvitesHttpService.getLocalizedTestInstructions(id);
        setTestInstructions(instructions);
        hasLoadedRef.current = true;
      } catch (e: any) {
        console.error(`testInstructions, ${e}`);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    };
    // Defer until the candidate leaves the intro (currentPage > 0): they must confirm the language
    // before starting, so the persisted locale is settled and the first fetch resolves it correctly.
    // Gate on currentPage, not invite.status — status isn't refreshed in memory after Start.
    if (inviteId && currentPage > 0 && !hasLoadedRef.current && !isFetchingRef.current) {
      fetchInstructions(inviteId);
    }
  }, [inviteId, testId, currentPage]);

  useEffect(() => {
    if (
      !isLoading &&
      questionsList &&
      questionsList.length > 0 &&
      questionsList[0]?.length !== 0 &&
      currentPage !== 0 &&
      currentPage <= questionsList.length
    ) {
      setIsOpenedInstructions(true);
    }
    // Adding currentPage as a dependency forces the modal to open on every page, which is not what we want.
    // This should be fixed eventually.
  }, [testId, questionsList, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isOpenedInstructions, setIsOpenedInstructions, testInstructions, isLoading };
}
